import React from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Download, Sheet } from "lucide-react";
import { getExportedColumnsData } from "../../../utils";

// Columns whose key/name marks them as a date (created_on, updatedOn, date, time, ...)
// so raw epoch-millis values don't show up as "1.77426E+12" in Excel.
const isDateLikeKey = (key = "") =>
  /_on$/.test(key) || /[a-z]On$/.test(key) || /date/i.test(key) || /time/i.test(key);

// Matches an already-formatted "DD-MM-YYYY, hh:mm AM/PM" (or legacy
// slash-separated) string so it's left alone instead of being re-parsed —
// `new Date("09/02/2026 ...")` is ambiguous (DD/MM vs the US MM/DD) and
// would silently corrupt callers that pre-format before handing off here.
const isAlreadyFormatted = (value) => /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}/.test(String(value).trim());

const formatIfDate = (column, value) => {
  if (value === null || value === undefined || value === "" || value === "NA") return value;
  if (!isDateLikeKey(column.key)) return value;
  if (isAlreadyFormatted(value)) return value;
  const numeric = Number(value);
  const date = !Number.isNaN(numeric) && String(value).trim() !== "" ? new Date(numeric) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n) => String(n).padStart(2, "0");
  let hours = date.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}, ${pad(hours)}:${pad(date.getMinutes())} ${ampm}`;
};

// Same green "Export to Excel" button style/placement used in Helpdesk/Grievance (My Tickets).
const ExportExcelButton = ({ exportData, exportColumns, exportFileName }) => {
  const handleDownload = async () => {
    const rows = getExportedColumnsData(exportColumns, exportData || []);
    const headers = exportColumns.map((col) => col.label);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    rows.forEach((row) => {
      const rowValues = exportColumns.map((col) => formatIfDate(col, row[col.label]));
      const dataRow = worksheet.addRow(rowValues);
      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    worksheet.columns.forEach((column) => {
      column.width = 22;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    now.setMinutes(now.getMinutes() + 330); // IST
    const istDate = now.toISOString().split("T")[0];
    const istTime = now.toISOString().split("T")[1].split(".")[0].replace(/:/g, "");

    saveAs(blob, `${exportFileName}_${istDate}_${istTime}.xlsx`);
  };

  if (!exportData || exportData.length === 0) return null;

  return (
    <button
      onClick={handleDownload}
      style={{
        marginLeft: "12px",
        padding: "8px 12px",
        backgroundColor: "#10B981",
        color: "white",
        border: "none",
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        cursor: "pointer",
        fontWeight: "500",
        height: "36px",
        fontSize: "12px",
      }}
      title="Export to Excel"
    >
      <Download size={14} />
      <Sheet size={14} />
    </button>
  );
};

export default ExportExcelButton;
